// src/api/locations.ts
import type { Location, LocationCreate, LocationResponse, LocationUpdate, LocationWithMemos, LocationsResponse } from './types';
import { createAuthenticatedFetch, type AssetExistsResponse } from './types/core/common';

// @deprecated Use imports from '@/api/types' instead
export type {
    Location,
    LocationCreate, LocationResponse, LocationUpdate, LocationsResponse
};

const api = createAuthenticatedFetch();

export const locations = {
  // ===============================
  // Méthodes Legacy (compatibilité)
  // ===============================
  
  async fetchAllLocations(): Promise<Location[]> {
    try {
      return await api.get('/locations/');
    } catch (error) {
      throw new Error("Erreur lors de la récupération des localisations");
    }
  },

  // ===============================
  // Nouvelles Méthodes Sécurisées
  // ===============================

  /**
   * Récupère les localisations de l'utilisateur connecté
   */
  async getMy(): Promise<Location[]> {
    return api.get<Location[]>('/locations/my');
  },

  /**
   * Récupère les localisations publiques seulement
   */
  async getPublic(): Promise<Location[]> {
    return api.get<Location[]>('/locations/public');
  },

  /**
   * Récupère les localisations accessibles (utilisateur + publiques)
   */
  async getAccessible(): Promise<Location[]> {
    return api.get<Location[]>('/locations/accessible');
  },

  /**
   * Récupère toutes les localisations (admin seulement)
   */
  async getAllAdmin(): Promise<Location[]> {
    return api.get<Location[]>('/locations/all');
  },

  /**
   * Récupère toutes les localisations (admin seulement), y compris soft-deleted
   */
  async getAllAdminWithDeleted(): Promise<Location[]> {
    return api.get<Location[]>('/locations/all_with_deleted');
  },

  // ===============================
  // Méthodes CRUD Sécurisées
  // ===============================

  async createLocation(location: LocationCreate): Promise<Location> {
    return api.post<Location>('/locations/', location);
  },

  async updateLocation(uuid: string, location: LocationUpdate): Promise<Location> {
    return api.put<Location>(`/locations/${uuid}`, location);
  },

  async deleteLocation(uuid: string): Promise<void> {
    return api.delete(`/locations/${uuid}`);
  },

  async hardDeleteLocation(uuid: string): Promise<void> {
    return api.delete(`/locations/hard_delete/${uuid}`);
  },

  async fetchLocationByGeonameId(geonameId: string): Promise<Location> {
    return api.get<Location>(`/locations/geoname/${geonameId}`);
  },

  async fetchLocationsByUser(userId: string): Promise<LocationWithMemos[]> {
    return api.get<LocationWithMemos[]>(`/locations/user/${userId}`);
  },

  async fetchAllLocationsAdmin(): Promise<Location[]> {
    return api.get<Location[]>(`/locations/admin/all`);
  },

  async fetchLocationByUuid(uuid: string): Promise<Location> {
    return api.get<Location>(`/locations/${uuid}`);
  },

  async fetchLocationByUuidWithMemos(uuid: string): Promise<LocationWithMemos> {
    return api.get<LocationWithMemos>(`/locations/withmemos/${uuid}`);
  },

  async fetchLocationById(uuid: string): Promise<Location> {
    return api.get<Location>(`/locations/${uuid}`);
  },

  async restoreLocation(uuid: string): Promise<Location> {
    return api.put<Location>(`/locations/restore/${uuid}`);
  },

  /**
   * Récupère une location publique par UUID (accès public)
   */
  async getPublicByUuid(uuid: string): Promise<Location> {
    try {
      return await api.get(`/locations/public/${uuid}`);
    } catch (error: unknown) {
      if ((error as any)?.status === 404) {
        throw new Error("Location publique non trouvée");
      }
      throw new Error("Erreur lors de la récupération de la location publique");
    }
  },

  /**
   * Récupère une location par UUID avec ses mémos (accès public ou par connaissance de l'UUID)
   */
  async getPublicByUuidWithMemos(uuid: string): Promise<LocationWithMemos> {
    try {
      return await api.get(`/locations/public/${uuid}/with-memos`);
    } catch (error: unknown) {
      if ((error as any)?.status === 404) {
        throw new Error("Location non trouvée");
      }
      throw new Error("Erreur lors de la récupération de la location et ses mémos");
    }
  },

  /**
   * Vérifie si une location existe par son UUID (public ou privé)
   */
  async checkExists(uuid: string): Promise<AssetExistsResponse> {
    try {
      return await api.get(`/locations/exists/${uuid}`);
    } catch (error: unknown) {
      if ((error as any)?.status === 404) {
        return {
          exists: false,
          is_owner: false,
          is_public: false
        };
      }
      throw new Error("Erreur lors de la vérification de l'existence de la location");
    }
  }
};

// Exporter les fonctions individuellement
export const {
  fetchAllLocations,
  createLocation,
  updateLocation,
  deleteLocation,
  fetchLocationByGeonameId,
  fetchLocationsByUser,
  fetchAllLocationsAdmin,
  fetchLocationByUuid,
  fetchLocationByUuidWithMemos,
  fetchLocationById,
  restoreLocation
} = locations;
